import React from 'react';
import GlossaryTooltip from './GlossaryTooltip';

interface BlogContentProps {
  html: string;
  lang: string;
  className?: string;
}

/**
 * Renders blog post HTML, transforming <span data-glossary-id="…"> elements
 * into interactive GlossaryTooltip components.
 *
 * Authoring convention (inserted by the admin picker):
 *   <span data-glossary-id="ENTRY_ID">display text</span>
 *
 * Everything else is rendered as-is via dangerouslySetInnerHTML on split chunks.
 *
 * Note: we use a data-* attribute on a standard <span> rather than a custom
 * element because Tiptap's HTML serialiser preserves data-* attributes on spans
 * but strips or escapes unknown element types like <glossary-term>.
 */
const BlogContent: React.FC<BlogContentProps> = ({ html, lang, className }) => {
  const parts = parseGlossaryTerms(html, lang);

  return (
    <div className={className}>
      {parts}
    </div>
  );
};

/**
 * Splits the raw HTML string on <span data-glossary-id="…">…</span> elements
 * and returns an array of React nodes: plain HTML chunks + GlossaryTooltip components.
 */
function parseGlossaryTerms(html: string, lang: string): React.ReactNode[] {
  // Matches <span data-glossary-id="SOME_ID">any text content</span>
  // The span may also carry other attributes (e.g. class) added by Tiptap — [^>]* handles that.
  const GLOSSARY_RE = /<span\s[^>]*data-glossary-id="([^"]+)"[^>]*>([\s\S]*?)<\/span>/gi;

  const nodes: React.ReactNode[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let keyCounter = 0;

  while ((match = GLOSSARY_RE.exec(html)) !== null) {
    const [fullMatch, entryId, innerText] = match; // entryId = data-glossary-id value, innerText = display text
    const matchStart = match.index;

    // Plain HTML before this match
    if (matchStart > lastIndex) {
      const chunk = html.slice(lastIndex, matchStart);
      nodes.push(
        <span
          key={`chunk-${keyCounter++}`}
          dangerouslySetInnerHTML={{ __html: chunk }}
        />
      );
    }

    // Glossary tooltip node
    nodes.push(
      <GlossaryTooltip key={`gt-${keyCounter++}`} entryId={entryId} lang={lang}>
        {/* innerText may contain HTML — render as-is inside the link */}
        <span dangerouslySetInnerHTML={{ __html: innerText }} />
      </GlossaryTooltip>
    );

    lastIndex = matchStart + fullMatch.length;
  }

  // Remaining plain HTML after the last match
  if (lastIndex < html.length) {
    nodes.push(
      <span
        key={`chunk-${keyCounter++}`}
        dangerouslySetInnerHTML={{ __html: html.slice(lastIndex) }}
      />
    );
  }

  // No glossary terms found — fall back to a single innerHTML node
  if (nodes.length === 0) {
    nodes.push(
      <span key="full" dangerouslySetInnerHTML={{ __html: html }} />
    );
  }

  return nodes;
}

export default BlogContent;
